import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import logoImg from '@/assets/img/logo.png';

export function TermsPage() {
  // Force dark mode on legal pages
  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);

  return (
    <div className="min-h-screen bg-[#282836] text-white">
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Back button */}
        <Link to="/">
          <Button variant="ghost" className="mb-8 text-zinc-400 hover:text-white hover:bg-zinc-800">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Login
          </Button>
        </Link>

        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <img src={logoImg} alt="RoniDoc AI" className="h-10 w-auto" />
          </div>
          <h1 className="text-4xl font-bold mb-4">Terms & Conditions</h1>
          <p className="text-zinc-400">Last updated: January 2026</p>
        </div>

        {/* Content */}
        <div className="prose prose-invert prose-zinc max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">1. Agreement to Terms</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              These Terms and Conditions ("Terms") constitute a legally binding agreement between you ("User", "you", or "your")
              and Data Corner (ABN: 12 345 678 901), trading as RoniDoc AI ("RoniDoc AI", "we", "us", or "our"),
              governing your access to and use of the RoniDoc AI platform and services ("Service").
            </p>
            <p className="text-zinc-300 leading-relaxed mb-4">
              By accessing or using our Service, you acknowledge that you have read, understood, and agree to be bound by
              these Terms. If you do not agree to these Terms, you must not access or use the Service.
            </p>
            <p className="text-zinc-300 leading-relaxed mb-4">
              These Terms are governed by the laws of the Commonwealth of Australia and the State of New South Wales.
              You agree to submit to the exclusive jurisdiction of the courts of New South Wales, Australia.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">2. Description of Service</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              RoniDoc AI is an AI-powered document intelligence platform that provides the following services:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li>Document upload, storage, and management in a secure knowledge base</li>
              <li>Natural language search and querying of uploaded documents</li>
              <li>AI-powered document analysis, summarisation, and insights</li>
              <li>Intelligent chat interface for document Q&A</li>
              <li>Image extraction and visual content analysis</li>
              <li>Topic classification and document organisation</li>
              <li>User and department management for organisations</li>
              <li>Document permission and access controls</li>
              <li>Analytics and reporting on document usage</li>
            </ul>
            <p className="text-zinc-300 leading-relaxed mb-4">
              The Service is provided on a subscription basis with various tiers offering different features and usage limits.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">3. Eligibility</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              To use the Service, you must:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4">
              <li>Be at least 18 years of age</li>
              <li>Have the legal capacity to enter into a binding contract</li>
              <li>Not be prohibited from using the Service under Australian law or any other applicable jurisdiction</li>
              <li>If registering on behalf of an organisation, have the authority to bind that organisation to these Terms</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">4. Account Registration and Security</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              To access certain features of the Service, you must create an account. When creating an account, you agree to:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li>Provide accurate, current, and complete registration information</li>
              <li>Maintain and promptly update your account information</li>
              <li>Maintain the confidentiality of your login credentials</li>
              <li>Use strong passwords and enable multi-factor authentication where available</li>
              <li>Notify us immediately of any unauthorised access to your account</li>
              <li>Accept full responsibility for all activities that occur under your account</li>
            </ul>
            <p className="text-zinc-300 leading-relaxed mb-4">
              We reserve the right to suspend or terminate accounts that violate these Terms or that we reasonably
              believe have been compromised.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">5. Acceptable Use Policy</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              You agree to use the Service only for lawful purposes and in accordance with these Terms. You must not:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li>Upload, transmit, or store any content that infringes intellectual property rights</li>
              <li>Upload content containing malware, viruses, or other harmful code</li>
              <li>Attempt to gain unauthorised access to our systems or other users' accounts</li>
              <li>Interfere with or disrupt the integrity or performance of the Service</li>
              <li>Use the Service for any illegal, fraudulent, or harmful activities</li>
              <li>Upload content that is defamatory, obscene, or violates any person's privacy</li>
              <li>Use automated systems or bots to access the Service without our written permission</li>
              <li>Reverse engineer, decompile, or attempt to extract the source code of the Service</li>
              <li>Resell, sublicense, or redistribute the Service without authorisation</li>
              <li>Use the Service in any manner that could damage our reputation or goodwill</li>
            </ul>
            <p className="text-zinc-300 leading-relaxed mb-4">
              Violation of this acceptable use policy may result in immediate termination of your account and
              potential legal action.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">6. Intellectual Property Rights</h2>

            <h3 className="text-xl font-medium text-white mb-3">Our Intellectual Property</h3>
            <p className="text-zinc-300 leading-relaxed mb-4">
              The Service, including its original content, features, functionality, design, and underlying technology,
              is owned by Data Corner and is protected by Australian and international copyright, trademark,
              patent, trade secret, and other intellectual property laws.
            </p>

            <h3 className="text-xl font-medium text-white mb-3">Your Content</h3>
            <p className="text-zinc-300 leading-relaxed mb-4">
              You retain all ownership rights in the documents and content you upload to the Service ("User Content").
              By uploading User Content, you grant us a non-exclusive, worldwide, royalty-free licence to:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li>Store, process, and transmit your User Content as necessary to provide the Service</li>
              <li>Create AI-generated analyses, summaries, and insights from your User Content</li>
              <li>Generate embeddings and search indices for your User Content</li>
              <li>Display your User Content to authorised users within your organisation</li>
            </ul>
            <p className="text-zinc-300 leading-relaxed mb-4">
              This licence terminates when you delete your User Content or close your account, except for content
              that has been shared with others or used in ways that cannot be reversed.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">7. AI-Generated Content and Limitations</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              The Service utilises artificial intelligence and machine learning technologies to analyse documents
              and generate responses. You acknowledge and agree that:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li>AI-generated content may contain errors, inaccuracies, or incomplete information</li>
              <li>AI responses should not be relied upon as professional advice (legal, financial, medical, etc.)</li>
              <li>You are responsible for verifying the accuracy of AI-generated content before acting upon it</li>
              <li>We do not guarantee the accuracy, completeness, or reliability of AI outputs</li>
              <li>AI capabilities and features may change or be updated without notice</li>
            </ul>
            <p className="text-zinc-300 leading-relaxed mb-4">
              We strongly recommend that you independently verify any critical information derived from the Service
              before making business decisions.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">8. Fees and Payment</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              Access to certain features of the Service requires payment of fees as specified on our pricing page.
              By subscribing to a paid plan, you agree to:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li>Pay all applicable fees in Australian dollars unless otherwise agreed</li>
              <li>Provide accurate billing information and authorise us to charge your payment method</li>
              <li>Automatic renewal of subscriptions unless cancelled before the renewal date</li>
              <li>Price changes will be communicated with at least 30 days' notice</li>
            </ul>
            <p className="text-zinc-300 leading-relaxed mb-4">
              All fees are exclusive of GST and other applicable taxes, which will be added where required.
              Refunds are provided in accordance with Australian Consumer Law.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">9. Data Privacy and Security</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              Your use of the Service is also governed by our Privacy Policy, which describes how we collect,
              use, and protect your personal information in compliance with the Privacy Act 1988 (Cth) and
              the Australian Privacy Principles.
            </p>
            <p className="text-zinc-300 leading-relaxed mb-4">
              We implement industry-standard security measures to protect your data, including:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4">
              <li>Encryption of data in transit (TLS 1.3) and at rest (AES-256)</li>
              <li>Secure cloud infrastructure hosted on AWS in the Asia Pacific (Sydney) region</li>
              <li>Regular security audits and penetration testing</li>
              <li>Multi-factor authentication support</li>
              <li>Role-based access controls</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">10. Service Availability and Modifications</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              While we strive to maintain high availability, we do not guarantee uninterrupted access to the Service.
              We reserve the right to:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li>Perform scheduled and emergency maintenance</li>
              <li>Modify, update, or discontinue features with reasonable notice</li>
              <li>Limit or restrict access during high-demand periods</li>
              <li>Suspend service to address security threats or violations of these Terms</li>
            </ul>
            <p className="text-zinc-300 leading-relaxed mb-4">
              We will endeavour to provide advance notice of significant changes or planned downtime where practicable.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">11. Termination</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              Either party may terminate the agreement at any time:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li>You may close your account at any time through the Service settings</li>
              <li>We may terminate or suspend your access immediately for violations of these Terms</li>
              <li>We may terminate with 30 days' notice for any other reason</li>
            </ul>
            <p className="text-zinc-300 leading-relaxed mb-4">
              Upon termination, your right to use the Service ceases immediately. You may request export of your
              data within 30 days of termination, after which your data will be deleted in accordance with our
              data retention policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">12. Limitation of Liability</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              To the maximum extent permitted by Australian law, including the Australian Consumer Law:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li>The Service is provided "as is" without warranties of any kind, except those that cannot be excluded by law</li>
              <li>We shall not be liable for any indirect, incidental, special, consequential, or punitive damages</li>
              <li>Our total liability shall not exceed the fees paid by you in the 12 months preceding the claim</li>
              <li>We are not liable for any loss of data, revenue, profits, or business opportunities</li>
            </ul>
            <p className="text-zinc-300 leading-relaxed mb-4">
              Nothing in these Terms excludes or limits liability that cannot be excluded or limited under Australian law,
              including liability for death or personal injury caused by negligence, fraud, or breach of consumer guarantees.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">13. Indemnification</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              You agree to indemnify and hold harmless RoniDoc AI, its affiliates, officers, directors, employees,
              and agents from any claims, damages, losses, or expenses (including reasonable legal fees) arising from:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4">
              <li>Your use of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any third-party rights, including intellectual property rights</li>
              <li>Any User Content you upload to the Service</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">14. Dispute Resolution</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              In the event of any dispute arising from these Terms or your use of the Service:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li>The parties shall first attempt to resolve the dispute through good faith negotiation</li>
              <li>If unresolved within 30 days, either party may refer the dispute to mediation</li>
              <li>If mediation fails, the dispute shall be resolved in the courts of New South Wales, Australia</li>
            </ul>
            <p className="text-zinc-300 leading-relaxed mb-4">
              Nothing in this clause prevents either party from seeking urgent injunctive relief where necessary.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">15. Changes to Terms</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              We reserve the right to modify these Terms at any time. We will notify you of material changes by:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li>Posting updated Terms on the Service with a new effective date</li>
              <li>Sending an email notification to your registered email address</li>
              <li>Displaying a prominent notice within the Service</li>
            </ul>
            <p className="text-zinc-300 leading-relaxed mb-4">
              Your continued use of the Service after the effective date of any changes constitutes acceptance
              of the modified Terms. If you do not agree to the changes, you must stop using the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">16. General Provisions</h2>

            <h3 className="text-xl font-medium text-white mb-3">Entire Agreement</h3>
            <p className="text-zinc-300 leading-relaxed mb-4">
              These Terms, together with our Privacy Policy, constitute the entire agreement between you and
              RoniDoc AI regarding the Service.
            </p>

            <h3 className="text-xl font-medium text-white mb-3">Severability</h3>
            <p className="text-zinc-300 leading-relaxed mb-4">
              If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions
              shall continue in full force and effect.
            </p>

            <h3 className="text-xl font-medium text-white mb-3">Waiver</h3>
            <p className="text-zinc-300 leading-relaxed mb-4">
              Our failure to enforce any right or provision of these Terms shall not constitute a waiver of
              such right or provision.
            </p>

            <h3 className="text-xl font-medium text-white mb-3">Assignment</h3>
            <p className="text-zinc-300 leading-relaxed mb-4">
              You may not assign or transfer your rights under these Terms without our prior written consent.
              We may assign our rights without restriction.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">17. Contact Information</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              If you have any questions about these Terms & Conditions, please contact us:
            </p>
            <div className="text-zinc-300 space-y-2">
              <p><strong className="text-white">Data Corner</strong> (trading as RoniDoc AI)</p>
              <p>Sydney, NSW, Australia</p>
              <p>Email: <a href="mailto:legal@ronidoc.com.au" className="text-primary hover:underline">legal@ronidoc.com.au</a></p>
              <p>Support: <a href="mailto:support@ronidoc.com.au" className="text-primary hover:underline">support@ronidoc.com.au</a></p>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-zinc-800 text-center text-sm text-zinc-500">
          &copy; {new Date().getFullYear()} Data Corner trading as RoniDoc AI. All rights reserved.
        </div>
      </div>
    </div>
  );
}
